<script setup lang="ts">
/**
 * Quickstart tab (T07) — static content only.
 *
 * Sections in order (matches reference/img_4.png, img_5.png, img_7.png, img_8.png):
 *   1. Authentication — `Authorization: Bearer …` header + link to /keys.
 *   2. Base URL — the canonical public proxy URL.
 *   3. Endpoints — static method/path/auth/description table.
 *   4. Code samples — Python / TypeScript / curl (copy-to-clipboard).
 *   5. Tool / Function Calling — Python example.
 *
 * No router fetches happen here; all data is hard-coded documentation.
 */
import CopyCodeBlock from './CopyCodeBlock.vue';
import CodeTabs from './CodeTabs.vue';
import { PUBLIC_PROXY_BASE } from '../../api/docs';

interface Endpoint {
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH';
  path: string;
  auth: string;
  description: string;
}

const ENDPOINTS: Endpoint[] = [
  { method: 'GET', path: '/health', auth: 'None', description: 'Health check' },
  { method: 'GET', path: '/v1/models', auth: 'None', description: 'List available models (OpenAI-compatible)' },
  { method: 'GET', path: '/api/models/capabilities', auth: 'None', description: 'Per-model context, tool/reasoning support, quantization, VRAM (live from chain)' },
  { method: 'GET', path: '/api/pricing', auth: 'None', description: 'Live USD per-token pricing for all models' },
  { method: 'POST', path: '/v1/chat/completions', auth: 'API Key', description: 'Chat completions (streaming and non-streaming)' },
  { method: 'POST', path: '/api/auth/register', auth: 'None', description: 'Create new account' },
  { method: 'POST', path: '/api/auth/login', auth: 'None', description: 'Sign in' },
  { method: 'GET', path: '/api/auth/me', auth: 'JWT', description: 'Get current user info' },
  { method: 'GET', path: '/api/keys', auth: 'JWT', description: 'List API keys' },
  { method: 'POST', path: '/api/keys', auth: 'JWT', description: 'Create new API key' },
  { method: 'DELETE', path: '/api/keys/:id', auth: 'JWT', description: 'Revoke an API key' },
  { method: 'GET', path: '/api/usage/summary', auth: 'JWT', description: 'Aggregated usage stats' },
  { method: 'GET', path: '/api/usage/daily', auth: 'JWT', description: 'Daily usage breakdown' },
  { method: 'GET', path: '/api/usage', auth: 'JWT', description: 'Detailed usage logs' },
];

const PYTHON_SAMPLE = `from openai import OpenAI

client = OpenAI(
    base_url="${PUBLIC_PROXY_BASE}",
    api_key="sk-your-api-key-here",
)

response = client.chat.completions.create(
    model="Qwen/Qwen3-235B-A22B-Instruct-2507-FP8",
    messages=[{"role": "user", "content": "Hello!"}],
)
print(response.choices[0].message.content)`;

const TYPESCRIPT_SAMPLE = `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${PUBLIC_PROXY_BASE}",
  apiKey: "sk-your-api-key-here",
});

const response = await client.chat.completions.create({
  model: "Qwen/Qwen3-235B-A22B-Instruct-2507-FP8",
  messages: [{ role: "user", content: "Hello!" }],
});
console.log(response.choices[0].message.content);`;

const CURL_SAMPLE = `# Qwen
curl ${PUBLIC_PROXY_BASE}/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-your-api-key-here" \\
  -d '{
    "model": "Qwen/Qwen3-235B-A22B-Instruct-2507-FP8",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`;

const TOOL_CALL_SAMPLE = `response = client.chat.completions.create(
    model="Qwen/Qwen3-235B-A22B-Instruct-2507-FP8",
    messages=[{"role": "user", "content": "What's the weather in Berlin?"}],
    tools=[{
        "type": "function",
        "function": {
            "name": "get_weather",
            "description": "Get current weather for a location",
            "parameters": {
                "type": "object",
                "properties": {
                    "location": {"type": "string"}
                },
                "required": ["location"]
            }
        }
    }],
)

tool_call = response.choices[0].message.tool_calls[0]
print(tool_call.function.name)       # "get_weather"
print(tool_call.function.arguments)   # '{"location": "Berlin"}'`;

const CHAT_SAMPLES = [
  { label: 'Python', language: 'python', code: PYTHON_SAMPLE },
  { label: 'TypeScript / Node.js', language: 'typescript', code: TYPESCRIPT_SAMPLE },
  { label: 'curl', language: 'bash', code: CURL_SAMPLE },
];

function methodClass(m: Endpoint['method']): string {
  switch (m) {
    case 'GET':
      return 'bg-surface-soft text-ok';
    case 'POST':
      return 'bg-surface-soft text-ink';
    case 'DELETE':
      return 'bg-surface-soft text-danger';
    case 'PATCH':
      return 'bg-surface-soft text-warn';
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Authentication -->
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Authentication</h2>
      <p class="text-sm text-muted mt-2">
        All proxy endpoints require an API key. Include it in the
        <code>Authorization</code> header:
      </p>
      <div class="mt-4">
        <CopyCodeBlock code="Authorization: Bearer sk-your-api-key-here" />
      </div>
      <p class="text-sm text-muted mt-4">
        Create API keys from the
        <router-link to="/keys" class="link-inline">API Keys</router-link>
        page. Each new account starts with
        <span class="text-ok font-semibold">1,000,000 free tokens</span>.
      </p>
    </section>

    <!-- Base URL -->
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Base URL</h2>
      <div class="mt-4">
        <CopyCodeBlock :code="PUBLIC_PROXY_BASE" />
      </div>
    </section>

    <!-- Endpoints table -->
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Endpoints</h2>
      <div class="mt-4 overflow-x-auto">
        <table class="table w-full">
          <thead>
            <tr>
              <th class="w-[100px]">Method</th>
              <th>Path</th>
              <th class="w-[120px]">Auth</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in ENDPOINTS" :key="`${e.method} ${e.path}`">
              <td>
                <span
                  :class="['inline-block text-[11px] font-mono px-2 py-0.5 rounded-full', methodClass(e.method)]"
                >{{ e.method }}</span>
              </td>
              <td>
                <code>{{ e.path }}</code>
              </td>
              <td class="text-sm text-muted">{{ e.auth }}</td>
              <td class="text-sm">{{ e.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Chat Completions samples -->
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Chat Completions</h2>
      <p class="text-sm text-muted mt-2">
        The main proxy endpoint. Fully compatible with the OpenAI Chat Completions API. Supports
        both streaming (<code>stream: true</code>) and non-streaming modes. Set
        <code>model</code> to any ID returned by <code>/v1/models</code>.
      </p>
      <div class="mt-4">
        <CodeTabs :tabs="CHAT_SAMPLES" />
      </div>
    </section>

    <!-- Tool / Function Calling -->
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Tool / Function Calling</h2>
      <p class="text-sm text-muted mt-2">
        The proxy supports OpenAI tool calling even when upstream nodes don't support it natively.
        When <code>SIMULATE_TOOL_CALLS=true</code> is configured, the proxy rewrites tool-call
        requests into structured prompts and parses the model's JSON response back into proper
        <code>tool_calls</code> format.
      </p>
      <div class="mt-4">
        <CopyCodeBlock :code="TOOL_CALL_SAMPLE" language="python" />
      </div>
    </section>
  </div>
</template>
